import { Component, OnInit } from "@angular/core";
import { environment } from "../../../environments/environment";
import {
  SubscriptionPlan,
  SubscriptionPlanItem
} from "../../models/subscriptionplan";
import { NgbModalRef, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { SubscriptionplanService } from "../../services/subscriptionplan.service";
import { SubscriptionplansFormComponent } from "../subscriptionplans-form/subscriptionplans-form.component";
import { SubscriptionplansDeleteComponent } from "../subscriptionplans-delete/subscriptionplans-delete.component";
import { fadeInAndOut } from "../../utils/animations-utils";
import { AccountService } from "../../services/account.service";
import { ToastsManager } from "ng2-toastr";
import { TranslateService } from "@ngx-translate/core";
import { TripPeriodPrice } from "../../models/trip-period-price";

@Component({
  selector: "app-subscriptionplans",
  animations: [fadeInAndOut()],
  templateUrl: "./subscriptionplans-list.component.html",
  styleUrls: ["./subscriptionplans-list.component.css"]
})
export class SubscriptionPlansListComponent implements OnInit {
  p = 1; // current page
  total: number; // total count of records
  itemsPerPage = environment.itemPerPage; // number of records to display on each page
  listLoading: boolean;

  filters: Map<string, any>;

  NAME_SORT = "s.name";
  GROUP_SORT = "groupName";
  USER_SORT = "userCount";
  DELETED_SORT = "s.status";
  NAME_FILTER = "s.name";
  GROUP_FILTER = "s.group.name";
  USER_FILTER = "userCount";

  /**
   * Keys : string criterias
   * Values : boolean => true if ascending, false otherwise
   */
  sortingCriterias: Map<string, boolean> = new Map();
  typeFilter = "ALL";
  listFiltered = false;

  /** SP to display on the table */
  sp: SubscriptionPlanItem[] = [];

  /** Modal windows */
  spToUpdate: SubscriptionPlanItem;
  spToDelete: SubscriptionPlanItem;
  deleteModal: NgbModalRef;
  formModal: NgbModalRef;

  downloading = false;

  constructor(
    private modalService: NgbModal,
    private spService: SubscriptionplanService,
    private toast: ToastsManager,
    private translateService: TranslateService,
    private accountService: AccountService
  ) {}

  get isSuperAdmin() {
    return this.accountService.isSuperAdmin();
  }

  /**
   * Loads first page of results ordered by ID
   */
  ngOnInit() {
    this.initFilters();
    if (localStorage.getItem("itemsPerPage")) {
      this.itemsPerPage = parseInt(localStorage.getItem("itemsPerPage"), 10);
    }
    this.getPage(1);
  }

  /**
   * Create a new empty object for filtering attachment points
   * Retrieve first page
   */
  clearFilters() {
    this.listFiltered = false;
    this.initFilters();
    this.reloadList();
  }

  /**
   * Sorting system function as follow
   * On first click on a criteria => add the criteria as ascending
   * On second click => put the critiera to descending
   * On third click => remove the criteria from the map
   */
  updateSortingCriterias(criteria: string) {
    if (this.sortingCriterias.has(criteria)) {
      if (this.sortingCriterias.get(criteria)) {
        // criteria is present as true => switch to false
        this.sortingCriterias.set(
          criteria,
          !this.sortingCriterias.get(criteria)
        );
      } else {
        // criteria present as false => delete it
        this.sortingCriterias.delete(criteria);
      }
    } else {
      this.sortingCriterias.set(criteria, true);
    }
  }

  initFilters() {
    this.filters = new Map();
  }

  changeItemCount(value) {
    this.itemsPerPage = value;
    this.reloadList();

    localStorage.setItem("itemsPerPage", value);
  }

  sortBy(criteria: string) {
    this.updateSortingCriterias(criteria);
    this.reloadList();
  }

  /**
   * Reload lists according to filters, sorting criterias from the first page
   */
  reloadList() {
    this.listFiltered = false;
    Object.values(this.filters).forEach(value => {
      if (value) {
        this.listFiltered = true;
      }
    });
    this.getPage(1);
  }

  getPage(page: number) {
    this.p = page;
    this.listLoading = true;
    this.spService
      .getPaginatedFilteredSortedSP(
        this.p,
        this.itemsPerPage,
        this.filters,
        this.sortingCriterias
      )
      .subscribe(data => {
        this.sp = data.res;
        this.total = data.headers.get("x-total-count");
        this.listLoading = false;
      });
  }

  /* ------------- Modals ------------------- */
  onNewClick(): void {
    this.formModal = this.modalService.open(SubscriptionplansFormComponent, {
      size: "lg",
      backdrop: "static"
    });
    this.formModal.componentInstance.creationMode = true;
    this.formModal.componentInstance.sp = new SubscriptionPlanItem();
    this.formModal.componentInstance.createValidation.subscribe(() =>
      this.getPage(this.p)
    );
  }

  onUpdateClick(sp) {
    this.spService.get(sp.planId).subscribe(subPlan => {
      this.formModal = this.modalService.open(SubscriptionplansFormComponent, {
        size: "lg",
        backdrop: "static"
      });
      subPlan.tripPeriodPrices = Array.from(
        new Set(subPlan.tripPeriodPrices.map(trip => trip.id))
      ).map(id => ({
        id,
        ...subPlan.tripPeriodPrices.find(trip => trip.id === id)
      })) as TripPeriodPrice[];
      this.formModal.componentInstance.creationMode = false;
      this.formModal.componentInstance.sp = subPlan;

      this.formModal.componentInstance.updateValidation.subscribe(result => {
        this.getPage(this.p);
      });
    });
  }

  onDeleteClick(l) {
    this.spToDelete = l;
    this.deleteModal = this.modalService.open(
      SubscriptionplansDeleteComponent,
      { centered: true }
    );
    this.deleteModal.componentInstance.sp = this.spToDelete;
    this.deleteModal.componentInstance.onDeleteValidation.subscribe(d =>
      this.getPage(this.p)
    );
  }

  downloadXLSFile() {
    this.downloading = true;
    this.spService
      .downloadXLSFile(
        this.p,
        this.itemsPerPage,
        this.filters,
        this.sortingCriterias
      )
      .subscribe(isTruncated => {
        if (isTruncated) {
          this.toast.warning(
            this.translateService.instant("common.downloadOk")
          );
        } else {
          this.toast.success(
            this.translateService.instant("common.downloadTruncated")
          );
        }
        this.downloading = false;
      });
  }
}



// WEBPACK FOOTER //
// ./src/app/subscriptionplans/subscriptionplans-list/subscriptionplans-list.component.ts